import { type SupplierPaymentStatusRow } from '@utazzo/shared';
import { UzButton, UzCard, UzInput, UzSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { getSupplierPaymentStatus } from '../invoices.api';
import { formatDateDMY, formatInrPaise, SettlementBadge } from '../invoices.helpers';
import { useSupplierPaymentStatus } from '../use-invoices';

import { downloadExcel, downloadPdf } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';
import { SupplierPicker } from '@/components/pickers/SupplierPicker';

/**
 * Supplier Payment Status (route /reports/supplier-payments): every supplier
 * payable line with its paid / partial / unpaid status. Filters: status,
 * supplier, date range, search. Exports to Excel + PDF.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'UNPAID', label: 'Unpaid' },
];

const rupees = (paise: string): number => Number(paise) / 100;

const EXPORT_COLUMNS = [
  { header: 'Proforma No.', value: (r: SupplierPaymentStatusRow) => r.proformaNumber ?? '' },
  { header: 'Invoice No.', value: (r: SupplierPaymentStatusRow) => r.invoiceNumber ?? '' },
  { header: 'Date', value: (r: SupplierPaymentStatusRow) => formatDateDMY(r.invoiceDate) },
  { header: 'Customer', value: (r: SupplierPaymentStatusRow) => r.customerName ?? '' },
  { header: 'Agent', value: (r: SupplierPaymentStatusRow) => r.agentName ?? '' },
  { header: 'Supplier', value: (r: SupplierPaymentStatusRow) => r.supplierName ?? '' },
  { header: 'Payable', value: (r: SupplierPaymentStatusRow) => rupees(r.payablePaise) },
  { header: 'Paid', value: (r: SupplierPaymentStatusRow) => rupees(r.amountSettledPaise) },
  { header: 'Outstanding', value: (r: SupplierPaymentStatusRow) => rupees(r.outstandingPaise) },
  { header: 'Status', value: (r: SupplierPaymentStatusRow) => r.settlementStatus },
];

export const SupplierPaymentsPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [supplierId, setSupplierId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');

  const query = {
    from,
    to,
    ...(supplierId ? { supplierId } : {}),
    ...(status ? { status: status as 'PAID' | 'PARTIAL' | 'UNPAID' } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  };
  const reportQuery = useSupplierPaymentStatus(query);
  const data = reportQuery.data;
  const rows = data?.rows ?? [];

  const handleExport = async (fmt: 'xlsx' | 'pdf'): Promise<void> => {
    try {
      const full = await getSupplierPaymentStatus(
        query as Parameters<typeof getSupplierPaymentStatus>[0],
      );
      if (fmt === 'pdf')
        downloadPdf(
          full.rows,
          EXPORT_COLUMNS,
          'supplier-payment-status',
          'Supplier Payment Status',
        );
      else downloadExcel(full.rows, EXPORT_COLUMNS, 'supplier-payment-status');
    } catch {
      toast.error('Failed to export');
    }
  };

  return (
    <PageShell
      title="Supplier Payment Status"
      description="Every supplier payable line with its paid / partial / unpaid status."
      actions={
        <div className="flex items-center gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={rows.length === 0}
            onClick={() => void handleExport('pdf')}
          >
            PDF
          </UzButton>
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={rows.length === 0}
            onClick={() => void handleExport('xlsx')}
          >
            Excel
          </UzButton>
        </div>
      }
    >
      <div className="mb-3">
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search supplier / customer / invoice / proforma..."
          containerClassName="max-w-md"
        />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FyRangeControls state={fyState} />
        <UzSelect
          label="Status"
          value={status}
          onValueChange={setStatus}
          options={STATUS_OPTIONS}
          containerClassName="w-44"
        />
        <div className="w-64">
          <SupplierPicker
            label="Supplier (all if blank)"
            value={supplierId || null}
            onValueChange={(v) => setSupplierId(v ?? '')}
          />
        </div>
        <UzButton variant="outline" onClick={() => void reportQuery.refetch()}>
          Refresh
        </UzButton>
      </div>

      <UzCard padding="none">
        {reportQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading report...</p>
        ) : reportQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the report.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No supplier lines match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-3 py-3">S.No</th>
                  <th className="px-3 py-3">Proforma No.</th>
                  <th className="px-3 py-3">Invoice No.</th>
                  <th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Supplier</th>
                  <th className="px-3 py-3 text-right">Payable</th>
                  <th className="px-3 py-3 text-right">Paid</th>
                  <th className="px-3 py-3 text-right">Outstanding</th>
                  <th className="px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.invoiceSupplierId}
                    className="border-b border-zinc-100 last:border-0"
                  >
                    <td className="px-3 py-3 text-zinc-600 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-3 text-zinc-700 tabular-nums">
                      {row.proformaNumber ?? '-'}
                    </td>
                    <td className="px-3 py-3 tabular-nums">
                      {row.invoiceNumber ? (
                        <Link
                          to={`/invoices/${row.invoiceId}`}
                          className="text-(--primary) hover:underline"
                        >
                          {row.invoiceNumber}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-3 text-zinc-700 tabular-nums">
                      {formatDateDMY(row.invoiceDate)}
                    </td>
                    <td className="px-3 py-3 text-zinc-700">{row.customerName ?? '-'}</td>
                    <td className="px-3 py-3 text-zinc-700">{row.supplierName ?? '-'}</td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {formatInrPaise(row.payablePaise)}
                    </td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {formatInrPaise(row.amountSettledPaise)}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-zinc-900 tabular-nums">
                      {formatInrPaise(row.outstandingPaise)}
                    </td>
                    <td className="px-3 py-3">
                      <SettlementBadge status={row.settlementStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
              {data && (
                <tfoot>
                  <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                    <td className="px-3 py-3" colSpan={6}>
                      Totals
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(data.totalPayablePaise)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(data.totalSettledPaise)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(data.totalOutstandingPaise)}
                    </td>
                    <td className="px-3 py-3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </UzCard>
    </PageShell>
  );
};
